import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import { BusinessIdea, RiskGuardDeepDive, LaunchMathDeepDive, SiteReconDeepDive, ScalingStrategyDeepDive, IntelligenceHubData, OperationsHubData, CollaborationHubData } from '@/src/types';
import { useAuth } from '@/src/contexts/AuthContext';
import { useReporting } from '@/src/hooks/useReporting';
import { generateRiskGuardDeepDive, generateLaunchMathDeepDive, generateSiteReconDeepDive, generateScalingStrategyDeepDive, generateIntelligenceHub, generateOperationsHub, generateCollaborationHub, generateMarketAnalysis } from '@/src/services/apiServices';
import { generateVentureId } from '@/src/utils/formatters';
import { db, doc, getDoc, setDoc, deleteDoc, handleFirestoreError, OperationType, auth } from '../firebase';
import NavigationSuite, { ParentTabType, ChildTabType } from './NavigationSuite';
import LivingSwot from './LivingSwot';
import OptimizeUpgradeFlow from './OptimizeUpgradeFlow';
import DisclaimerTooltip from './DisclaimerTooltip';
import { verifyCompetitorDeepDive } from '@/src/services/verificationService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { RefreshCw } from 'lucide-react';

interface Props {
  idea: BusinessIdea;
  onClose: () => void;
  isSaved: boolean;
  onToggleSave: () => void;
  onUpgradePrompt: () => void;
}

type Timeframe = '6m' | '1y' | '3y';

const LockedHubView: React.FC<{ 
  title: string; 
  description: string; 
  requiredTier: string;
  onUpgrade: () => void;
}> = ({ title, description, requiredTier, onUpgrade }) => (
  <div className="flex flex-col items-center justify-center py-20 px-8 text-center space-y-8 bg-[#322D29] border border-white/[0.06] rounded-xl animate-fade-in text-white">
    <div className="w-16 h-16 bg-[#3a3430] border border-white/10 rounded-xl flex items-center justify-center">
      <i className="fas fa-lock text-[#F26522] text-xl"></i>
    </div>
    <div className="max-w-md space-y-4">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">{title} Restricted</h3>
      <p className="text-xl font-bold text-white leading-tight font-heading">{description}</p>
      <p className="text-sm text-white/60 font-dm">This intelligence hub requires a <span className="font-bold text-white uppercase tracking-widest text-[10px]">{requiredTier}</span> subscription to access.</p>
    </div>
    <button 
      onClick={onUpgrade}
      className="px-8 py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-[0.2em] font-heading rounded-lg transition-all cursor-pointer"
    >
      Upgrade to {requiredTier}
    </button>
  </div>
);

const LockedLegalView: React.FC = () => {
  const triggerModal = () => {
    window.dispatchEvent(new CustomEvent('trigger-legal-modal'));
  };
  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center space-y-8 bg-[#322D29] border border-white/[0.06] rounded-xl animate-fade-in text-white">
      <div className="w-16 h-16 bg-[#3a3430] border border-white/10 rounded-xl flex items-center justify-center">
        <i className="fas fa-file-signature text-[#F26522] text-xl"></i>
      </div>
      <div className="max-w-md space-y-4">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Authorization Required</h3>
        <p className="text-xl font-bold text-white leading-tight font-heading">Legal Disclaimer Acknowledgment Pending</p>
        <p className="text-xs text-white/60 max-w-sm mx-auto leading-relaxed font-dm">
          Access to eNvesta AI's premium algorithmic estimates requires a one-time consent confirmation. Please verify that you understand eNvesta AI is not a financial adviser or broker.
        </p>
      </div>
      <button 
        onClick={triggerModal}
        className="px-8 py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-[0.2em] font-heading rounded-lg transition-all cursor-pointer"
      >
        Sign Legal Acknowledgment
      </button>
    </div>
  );
};

const BusinessDetail: React.FC<Props> = ({ idea, onClose, isSaved, onToggleSave, onUpgradePrompt }) => {
  const { 
    isLoggedIn, 
    onSignUpPrompt, 
    userProfile,
    optimizeActive,
    shouldShowOptimizeNudge,
    snoozeOptimizeNudge,
    legalAcknowledged,
    isPremium: authIsPremium,
    isOptimize: authIsOptimize,
    isTeam: authIsTeam,
    isEnterprise: authIsEnterprise
  } = useAuth();
  const { exportBusinessSnapshot } = useReporting();
  const [activeParentTab, setActiveParentTab] = useState<ParentTabType>('launchpad');
  const [activeChildTab, setActiveChildTab] = useState<ChildTabType>('gaps');
  const [mathMode, setMathMode] = useState<'full' | 'lean'>('full');
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [riskData, setRiskData] = useState<RiskGuardDeepDive | null>(null);
  const [loadingRisk, setLoadingRisk] = useState(false);
  const [mathData, setMathData] = useState<LaunchMathDeepDive | null>(null);
  const [loadingMath, setLoadingMath] = useState(false);
  const [reconData, setReconData] = useState<SiteReconDeepDive | null>(null);
  const [loadingRecon, setLoadingRecon] = useState(false);
  const [scalingData, setScalingData] = useState<ScalingStrategyDeepDive | null>(null);
  const [loadingScaling, setLoadingScaling] = useState(false);
  const [intelligenceData, setIntelligenceData] = useState<IntelligenceHubData | null>(null);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);
  const [operationsData, setOperationsData] = useState<OperationsHubData | null>(null);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [collaborationData, setCollaborationData] = useState<CollaborationHubData | null>(null);
  const [loadingCollaboration, setLoadingCollaboration] = useState(false);
  const [marketAnalysisData, setMarketAnalysisData] = useState<string | null>(null);
  const [loadingMarketAnalysis, setLoadingMarketAnalysis] = useState(false);
  const [isOptimizeUpgradeOpen, setIsOptimizeUpgradeOpen] = useState(false);
  const [competitorVerification, setCompetitorVerification] = useState<{ pricingVerified?: string; recentOpenings?: string[]; sentimentShift?: string; lastVerified?: string } | null>(null);
  const [verifyingCompetitor, setVerifyingCompetitor] = useState(false);

  // Operations Hub interactive states
  const [benchmarkMetric, setBenchmarkMetric] = useState<'revenue' | 'customers'>('revenue');
  const [scenarioFunding, setScenarioFunding] = useState(250);
  const [scenarioMarketShift, setScenarioMarketShift] = useState(15);
  const [scenarioRegulatory, setScenarioRegulatory] = useState(1);
  const [scenarioReport, setScenarioReport] = useState<string | null>(null);
  const [generatingScenario, setGeneratingScenario] = useState(false);
  const [actualsData] = useState([
    { month: 'Jan', ai: 4500, actual: 4200, customers: 120 },
    { month: 'Feb', ai: 5200, actual: 5100, customers: 145 },
    { month: 'Mar', ai: 6100, actual: 6400, customers: 180 },
    { month: 'Apr', ai: 7200, actual: 7100, customers: 210 },
    { month: 'May', ai: 8500, actual: 9200, customers: 280 },
    { month: 'Jun', ai: 10200, actual: 11500, customers: 340 },
  ]);
  const [regeneratingOps, setRegeneratingOps] = useState(false);
  
  // CRM Sync Console states
  const [selectedCrm, setSelectedCrm] = useState<'hubspot' | 'salesforce'>('hubspot');
  const [crmApiKey, setCrmApiKey] = useState('');
  const [crmInstanceUrl, setCrmInstanceUrl] = useState('');
  const [crmSandbox, setCrmSandbox] = useState(true);
  const [crmSyncing, setCrmSyncing] = useState(false);
  const [crmSyncResult, setCrmSyncResult] = useState<any | null>(null);
  const [crmError, setCrmError] = useState<string | null>(null);
  
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Dev override: allow localStorage to override Firestore tier for testing
  const devTier = localStorage.getItem('envesta-dev-tier');
  const subscriptionTier = (devTier || userProfile?.subscriptionTier || 'free').toString().toLowerCase();
  // Use AuthContext flags (which also respect dev override) OR local check
  const isPremium = authIsPremium || ['premium', 'optimize', 'team', 'enterprise'].includes(subscriptionTier);
  const isOptimize = authIsOptimize || ['optimize', 'team', 'enterprise'].includes(subscriptionTier);
  const isTeam = authIsTeam || ['team', 'enterprise'].includes(subscriptionTier);

  const effectiveIdeaId = useMemo(() => {
    return generateVentureId(idea.title, idea.locationName, idea.id);
  }, [idea.title, idea.locationName, idea.id]);

  // Force-regenerate operations hub (bypasses stale Firestore cache)
  const regenerateOperations = async () => {
    if (!auth.currentUser || !effectiveIdeaId) {
      setOperationsData(null);
      setLoadingOperations(true);
      return;
    }
    setRegeneratingOps(true);
    setOperationsData(null);
    setLoadingOperations(true);
    try {
      const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/operations`;
      await deleteDoc(doc(db, divePath));
    } catch (e) { /* ignore */ }
    setRegeneratingOps(false);
  };
  const showLegalUnacknowledgedState = isPremium && !legalAcknowledged;

  // Reset all deep-dive states whenever target idea changes
  useEffect(() => {
    setRiskData(null);
    setMathData(null);
    setReconData(null);
    setScalingData(null);
    setIntelligenceData(null);
    setOperationsData(null);
    setCollaborationData(null);
    setMarketAnalysisData(null);
    setScenarioReport(null);
    setCompetitorVerification(null);
  }, [idea?.id, idea?.title, effectiveIdeaId]);

  // Reset scroll position when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [activeParentTab, activeChildTab]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeChildTab === 'risk' && !riskData && !loadingRisk) {
      const fetchRiskData = async () => {
        setLoadingRisk(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/risk`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setRiskData(JSON.parse(docData.content));
                  setLoadingRisk(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch risk deep dive from Firestore:', err);
            }
          }

          const data = await generateRiskGuardDeepDive(idea, idea.locationName || 'this district') as RiskGuardDeepDive;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/risk`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'risk',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Risk analysis failed:', error);
        } finally {
          setLoadingRisk(false);
        }
      };
      fetchRiskData();
    }
  }, [activeChildTab, idea, riskData, loadingRisk, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeChildTab === 'math' && !mathData && !loadingMath) {
      const fetchMathData = async () => {
        setLoadingMath(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/math`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setMathData(JSON.parse(docData.content));
                  setLoadingMath(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch math deep dive from Firestore:', err);
            }
          }

          const data = await generateLaunchMathDeepDive(idea, idea.locationName || 'this district') as LaunchMathDeepDive;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/math`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'math',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Launch math analysis failed:', error);
        } finally {
          setLoadingMath(false);
        }
      };
      fetchMathData();
    }
  }, [activeChildTab, idea, mathData, loadingMath, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeChildTab === 'sites' && !reconData && !loadingRecon) {
      const fetchReconData = async () => {
        setLoadingRecon(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/sites`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setReconData(JSON.parse(docData.content));
                  setLoadingRecon(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch site recon deep dive from Firestore:', err);
            }
          }

          const data = await generateSiteReconDeepDive(idea, idea.locationName || 'this district') as SiteReconDeepDive;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/sites`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'sites',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Site recon analysis failed:', error);
        } finally {
          setLoadingRecon(false);
        }
      };
      fetchReconData();
    }
  }, [activeChildTab, idea, reconData, loadingRecon, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeChildTab === 'strategy' && !scalingData && !loadingScaling) {
      const fetchScalingData = async () => {
        setLoadingScaling(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/strategy`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setScalingData(JSON.parse(docData.content));
                  setLoadingScaling(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch scaling strategy deep dive from Firestore:', err);
            }
          }

          const data = await generateScalingStrategyDeepDive(idea, idea.locationName || 'this district') as ScalingStrategyDeepDive;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/strategy`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'strategy',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Scaling strategy analysis failed:', error);
        } finally {
          setLoadingScaling(false);
        }
      };
      fetchScalingData();
    }
  }, [activeChildTab, idea, scalingData, loadingScaling, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeParentTab === 'intelligence' && !intelligenceData && !loadingIntelligence) {
      const fetchIntelligenceData = async () => {
        setLoadingIntelligence(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/intelligence`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setIntelligenceData(JSON.parse(docData.content));
                  setLoadingIntelligence(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch intelligence hub from Firestore:', err);
            }
          }

          const data = await generateIntelligenceHub(idea, idea.locationName || 'this district') as IntelligenceHubData;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/intelligence`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'intelligence',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Intelligence hub analysis failed:', error);
        } finally {
          setLoadingIntelligence(false);
        }
      };
      fetchIntelligenceData();
    }
  }, [activeParentTab, idea, intelligenceData, loadingIntelligence, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeParentTab === 'operations' && !operationsData && !loadingOperations) {
      const fetchOperationsData = async () => {
        setLoadingOperations(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/operations`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setOperationsData(JSON.parse(docData.content));
                  setLoadingOperations(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch operations hub from Firestore:', err);
            }
          }

          const data = await generateOperationsHub(idea, idea.locationName || 'this district') as OperationsHubData;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/operations`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'operations',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Operations hub analysis failed:', error);
        } finally {
          setLoadingOperations(false);
        }
      };
      fetchOperationsData();
    }
  }, [activeParentTab, idea, operationsData, loadingOperations, isLoggedIn, effectiveIdeaId]);

  useEffect(() => {
    if (showLegalUnacknowledgedState) return;
    if (activeParentTab === 'collaboration' && !collaborationData && !loadingCollaboration) {
      const fetchCollaborationData = async () => {
        setLoadingCollaboration(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/collaboration`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setCollaborationData(JSON.parse(docData.content));
                  setLoadingCollaboration(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch collaboration hub from Firestore:', err);
            }
          }

          const data = await generateCollaborationHub(idea, idea.locationName || 'this district') as CollaborationHubData;
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/collaboration`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'collaboration',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: JSON.stringify(data),
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Collaboration hub analysis failed:', error);
        } finally {
          setLoadingCollaboration(false);
        }
      };
      fetchCollaborationData();
    }
  }, [activeParentTab, idea, collaborationData, loadingCollaboration, isLoggedIn, effectiveIdeaId]);

  const regenerateMarketAnalysis = async () => {
    setLoadingMarketAnalysis(true);
    setMarketAnalysisData(null);
    try {
      if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
        const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/market_analysis`;
        try {
          await deleteDoc(doc(db, divePath));
        } catch (e) { /* ignore */ }
      }
      const data = await generateMarketAnalysis(idea, idea.locationName || 'this district');
      setMarketAnalysisData(data);
      if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
        const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/market_analysis`;
        try {
          await setDoc(doc(db, divePath), {
            sectionId: 'market_analysis',
            ventureTitle: idea.title || '',
            ventureLocation: idea.locationName || '',
            ventureId: effectiveIdeaId,
            content: data,
            updatedAt: new Date().toISOString()
          });
        } catch (err) {
          handleFirestoreError(err, OperationType.WRITE, divePath);
        }
      }
    } catch (error) {
      console.error('Failed to regenerate market analysis:', error);
    } finally {
      setLoadingMarketAnalysis(false);
    }
  };

  useEffect(() => {
    if (activeChildTab === 'gaps' && !marketAnalysisData && !loadingMarketAnalysis) {
      const fetchMarketAnalysis = async () => {
        setLoadingMarketAnalysis(true);
        try {
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/market_analysis`;
            try {
              const diveDoc = await getDoc(doc(db, divePath));
              if (diveDoc.exists()) {
                const docData = diveDoc.data();
                // Ensure cached doc specifically matches this venture title (prevents collided/generic cache bleed)
                const matchesTitle = docData?.ventureTitle &&
                  docData.ventureTitle.trim().toLowerCase() === (idea.title || '').trim().toLowerCase();
                if (matchesTitle && docData.content) {
                  setMarketAnalysisData(docData.content);
                  setLoadingMarketAnalysis(false);
                  return;
                }
              }
            } catch (err) {
              console.warn('Failed to fetch market analysis from Firestore:', err);
            }
          }

          const data = await generateMarketAnalysis(idea, idea.locationName || 'this district');
          setMarketAnalysisData(data);
          if (effectiveIdeaId && isLoggedIn && auth.currentUser) {
            const divePath = `users/${auth.currentUser.uid}/savedIdeas/${effectiveIdeaId}/deepDives/market_analysis`;
            try {
              await setDoc(doc(db, divePath), {
                sectionId: 'market_analysis',
                ventureTitle: idea.title || '',
                ventureLocation: idea.locationName || '',
                ventureId: effectiveIdeaId,
                content: data,
                updatedAt: new Date().toISOString()
              });
            } catch (err) {
              handleFirestoreError(err, OperationType.WRITE, divePath);
            }
          }
        } catch (error) {
          console.error('Market analysis failed:', error);
        } finally {
          setLoadingMarketAnalysis(false);
        }
      };
      fetchMarketAnalysis();
    }
  }, [activeChildTab, idea, marketAnalysisData, loadingMarketAnalysis, isLoggedIn, effectiveIdeaId]);

  const executeCrmSync = async () => {
    setCrmSyncing(true);
    setCrmError(null);
    setCrmSyncResult(null);

    try {
      const response = await fetch('/api/crm/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          crmType: selectedCrm,
          apiKey: crmApiKey,
          instanceUrl: crmInstanceUrl,
          idea: idea,
          userId: auth.currentUser?.uid || 'temp-anon',
          useSandbox: crmSandbox
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'CRM ledger rejected payload transaction.');
      }

      const data = await response.json();
      if (data.success) {
        setCrmSyncResult(data);
      } else {
        throw new Error(data.message || 'CRM connection pipeline unresponsive.');
      }
    } catch (err: any) {
      console.error('CRM Sync frontend exception:', err);
      setCrmError(err.message || 'An error occurred during synchronization.');
    } finally {
      setCrmSyncing(false);
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 8) return 'text-pure-red';
    if (score >= 5) return 'text-amber-600';
    return 'text-emerald-600';
  };

  const getShareUrl = () => {
    const url = new URL(window.location.origin);
    url.searchParams.set('idea', idea.title);
    return url.toString();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setShowShareMenu(false);
    }, 2000);
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(`Analyzing a new venture opportunity on eNvesta AI: ${idea.title}`);
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    setShowShareMenu(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#322D29] flex flex-col animate-slide-up overflow-hidden text-white selection:bg-[#F26522]/30 font-sans">
      <header className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] sticky top-0 z-[60]">
        <button onClick={onClose} className="flex items-center gap-2 text-white/60 hover:text-white transition-all group px-3 py-2 rounded-lg hover:bg-white/5 cursor-pointer">
          <i className="fas fa-chevron-left text-xs group-hover:-translate-x-1"></i>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] font-heading">Exit Protocol</span>
        </button>
        <div className="flex items-center gap-3">
          <button 
            onClick={onToggleSave}
            className={`px-4 py-2 rounded-lg border text-[9px] font-bold uppercase tracking-widest transition-all font-heading cursor-pointer ${
              isSaved ? 'bg-[#F26522]/20 border-[#F26522] text-[#F26522]' : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <i className={`fa-${isSaved ? 'solid' : 'regular'} fa-heart mr-2`}></i>
            {isSaved ? 'Opportunity Saved' : 'Save Opportunity'}
          </button>
          
          <div className="relative" ref={shareMenuRef}>
            <button 
              onClick={() => setShowShareMenu(!showShareMenu)} 
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all border cursor-pointer ${showShareMenu ? 'bg-[#F26522] text-white border-[#F26522]' : 'text-white/60 border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5'}`}
            >
              <i className="fas fa-share-nodes text-sm"></i>
            </button>
            
            {showShareMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-2xl z-[70] p-1 animate-in fade-in slide-in-from-top-2">
                <div className="p-2 border-b border-white/[0.06]">
                  <span className="text-[8px] font-bold uppercase tracking-widest text-[#F26522] block px-2 py-0.5 font-heading">Share Launchpad</span>
                </div>
                <button onClick={shareToTwitter} className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 group rounded-lg cursor-pointer">
                  <i className="fa-brands fa-x-twitter text-white/40 group-hover:text-white transition-colors"></i>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/80 font-heading">X (Twitter)</span>
                </button>
                <button onClick={shareToLinkedIn} className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 group rounded-lg cursor-pointer">
                  <i className="fa-brands fa-linkedin text-white/40 group-hover:text-sky-400 transition-colors"></i>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/80 font-heading">LinkedIn</span>
                </button>
                <button onClick={handleCopyLink} className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-3 group border-t border-white/[0.06] rounded-lg cursor-pointer">
                  <i className={`fas ${copied ? 'fa-check text-emerald-400' : 'fa-link text-white/40'} transition-colors`}></i>
                  <span className={`text-[9px] font-bold uppercase tracking-widest font-heading ${copied ? 'text-emerald-400' : 'text-white/80'}`}>
                    {copied ? 'Link Copied' : 'Copy Link'}
                  </span>
                </button>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              if (!isLoggedIn) {
                onSignUpPrompt();
                return;
              }
              if (!isPremium) {
                onUpgradePrompt();
                return;
              }
              exportBusinessSnapshot({
                ...idea,
                scalingStrategyDeepDive: scalingData || idea.scalingStrategyDeepDive,
                intelligenceHub: intelligenceData || idea.intelligenceHub,
                operationsHub: operationsData || idea.operationsHub,
                collaborationHub: collaborationData || idea.collaborationHub,
                riskGuardDeepDive: riskData || idea.riskGuardDeepDive,
                launchMathDeepDive: mathData || idea.launchMathDeepDive,
                siteReconDeepDive: reconData || idea.siteReconDeepDive
              });
            }} 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white/60 border border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
            title="Download Intelligence Snapshot PDF"
          >
            <i className="fas fa-download text-sm"></i>
          </button>
        </div>
      </header>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar bg-[#322D29]">
        <section className="px-4 sm:px-8 pt-8 md:pt-14 pb-10 max-w-4xl mx-auto border-b border-white/[0.06] bg-[#322D29]">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-lg bg-[#F26522] text-white">
              <span className="w-2 h-2 rounded-full animate-pulse bg-white"></span>
              <span className="text-[9px] font-bold uppercase tracking-[0.25em] font-heading">Venture Architecture Launchpad</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight text-white font-heading">{idea.title}</h1>
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-lg font-heading bg-white/5 border border-white/10 text-[#F26522]">{idea.category}</span>
                <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest font-heading">Launchpad Blueprint</span>
              </div>
            </div>
          </div>
        </section>

        <NavigationSuite 
          activeParentTab={activeParentTab}
          setActiveParentTab={setActiveParentTab}
          activeChildTab={activeChildTab}
          setActiveChildTab={setActiveChildTab}
          subscriptionTier={subscriptionTier}
        />

        <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 md:py-12 pb-40">
          {activeParentTab === 'launchpad' && activeChildTab === 'gaps' && (
            <div className="space-y-16 animate-fade-in">
              <div className="space-y-4">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Launchpad Narrative</h3>
                <p className="text-xl sm:text-2xl font-bold leading-relaxed text-white tracking-tight italic border-l-4 border-[#F26522] pl-6 sm:pl-8 font-dm">{idea.description}</p>
              </div>
              
              {loadingMarketAnalysis ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-8 h-8 border-2 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Generating Deep Market Analysis...</p>
                </div>
              ) : marketAnalysisData && (
                <div className="space-y-6 p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Deep Market Analysis</h3>
                    <button
                      type="button"
                      onClick={regenerateMarketAnalysis}
                      disabled={loadingMarketAnalysis}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-white/60 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded transition-colors disabled:opacity-40"
                      title="Re-run market analysis specifically for this venture"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingMarketAnalysis ? 'animate-spin text-[#F26522]' : ''}`} />
                      <span>Recalibrate</span>
                    </button>
                  </div>
                  <div className="text-sm font-medium leading-relaxed text-white/80 space-y-4 font-dm">
                    {marketAnalysisData.split('\n').map((para, i) => {
                      if (!para) return null;
                      return <p key={`market-analysis-para-${i}`}>{para}</p>;
                    })}
                  </div>
                </div>
              )}
              {idea.competition && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 sm:p-8 rounded-xl bg-[#3a3430] border border-white/[0.06] space-y-3">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">The Incumbent</span>
                    <h5 className="text-xl font-bold text-white font-heading">{idea.competition.nearestCompetitor}</h5>
                    <p className="text-sm font-medium text-white/60 font-dm">{idea.competition.distance} away | {idea.competition.competitorCapacity}</p>
                  </div>
                  <div className="p-6 sm:p-8 rounded-xl bg-[#322D29] border border-[#F26522]/30 space-y-3">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">The Lever</span>
                    <h5 className="text-xl font-bold text-white font-heading">Strategic Advantage</h5>
                    <p className="text-sm font-medium text-white/80 font-dm">{idea.competition.ourAdvantage}</p>
                  </div>

                  {/* Real-time Web Competitor Verification Panel */}
                  <div className="col-span-1 md:col-span-2 p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Competitor Intelligence Audit</h4>
                        <p className="text-[10px] font-medium text-white/50 italic mt-0.5 font-dm">Scans web index to verify pricing ranges, sentiment shifts, recently launched actions and competitor openings.</p>
                      </div>
                      
                      {!competitorVerification ? (
                        <button
                          onClick={async () => {
                            if (!auth.currentUser) return;
                            setVerifyingCompetitor(true);
                            try {
                              const res = await verifyCompetitorDeepDive(idea.title, idea.locationName || 'Kampala', auth.currentUser.uid);
                              setCompetitorVerification(res);
                            } catch (err) {
                              console.error(err);
                            } finally {
                              setVerifyingCompetitor(false);
                            }
                          }}
                          disabled={verifyingCompetitor}
                          className="px-4 py-2.5 bg-[#F26522] leading-none text-white text-[9px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 cursor-pointer transition-all disabled:opacity-50 rounded-lg font-heading"
                        >
                          {verifyingCompetitor ? (
                            <>
                              <i className="fas fa-circle-notch animate-spin mr-2"></i>
                              Auditing Competitor Data...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-globe mr-2"></i>
                              Run Live Competitor Audit
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">
                          ● Audit Completed • Last Verified: {competitorVerification.lastVerified}
                        </span>
                      )}
                    </div>

                    {competitorVerification && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 border-t border-white/[0.06]">
                        <div className="space-y-1">
                          <span className="text-[9px] font-bold uppercase text-white/40 tracking-wider">Pricing Verification</span>
                          <p className="text-xs font-medium text-white/80 leading-normal font-dm">{competitorVerification.pricingVerified}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[9px] font-bold uppercase text-white/40 tracking-wider">Recent Openings (30 Days)</span>
                          <div className="space-y-1">
                            {competitorVerification.recentOpenings?.map((open, idx) => (
                              <p key={`opening-${idx}`} className="text-xs font-medium text-white/80 leading-normal flex items-start gap-1 font-dm">
                                <span className="text-[#F26522] font-bold">•</span>
                                {open}
                              </p>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[9px] font-bold uppercase text-white/40 tracking-wider">Sentiment Audit</span>
                          <p className="text-xs font-medium text-white/80 leading-normal font-dm">{competitorVerification.sentimentShift}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeParentTab === 'launchpad' && activeChildTab === 'math' && (
            !isPremium ? (
              <LockedHubView 
                title="Launch Math" 
                description="Unlock deep-dive financial modeling, break-even analysis, and CapEx/OpEx projections."
                requiredTier="Premium"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-16 animate-fade-in">
              <div className="flex flex-col items-center space-y-6">
                <div className="text-center space-y-2">
                  <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-white/40 font-heading">Launchpad Protocol</h3>
                  <p className="text-xs font-medium text-white/60 max-w-xs mx-auto font-dm">Toggle between a full-scale institutional launch or a lightweight validation-first entry.</p>
                </div>
                <div className="inline-flex p-1 bg-[#3a3430] rounded-lg border border-white/[0.06]">
                  <button 
                    onClick={() => setMathMode('full')} 
                    className={`flex items-center gap-3 px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all font-heading cursor-pointer ${mathMode === 'full' ? 'bg-[#F26522] text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                  >
                    Full Capital
                  </button>
                  <button 
                    onClick={() => setMathMode('lean')} 
                    className={`flex items-center gap-3 px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all font-heading cursor-pointer ${mathMode === 'lean' ? 'bg-[#F26522] text-white shadow-lg' : 'text-white/60 hover:text-white'}`}
                  >
                    Lean Entry
                  </button>
                </div>
              </div>

              {loadingMath ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Running Deep-Dive Financial Analysis...</p>
                </div>
              ) : mathData ? (
                <div className="space-y-12">
                  {/* Phase 1: Startup Capital */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] rounded-xl flex items-center justify-center text-base">
                        <i className="fas fa-vault"></i>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 font-heading leading-none">Phase 1</h3>
                        <DisclaimerTooltip className="block mt-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading block">Startup Capital Breakdown</span>
                        </DisclaimerTooltip>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{mathData.startupCapital}</p>
                    </div>
                  </div>

                  {/* Phase 2: Monthly OpEx */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] rounded-xl flex items-center justify-center text-base">
                        <i className="fas fa-calendar-check"></i>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 font-heading leading-none">Phase 2</h3>
                        <DisclaimerTooltip className="block mt-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading block">Monthly Operational Expenditure</span>
                        </DisclaimerTooltip>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{mathData.monthlyOpEx}</p>
                    </div>
                  </div>

                  {/* Phase 3: Revenue Streams */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] rounded-xl flex items-center justify-center text-base">
                        <i className="fas fa-money-bill-trend-up"></i>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 font-heading leading-none">Phase 3</h3>
                        <DisclaimerTooltip className="block mt-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading block">Strategic Revenue Streams</span>
                        </DisclaimerTooltip>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{mathData.revenueStreams}</p>
                    </div>
                  </div>

                  {/* Phase 4: Break-Even Analysis */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] rounded-xl flex items-center justify-center text-base">
                        <i className="fas fa-flag-checkered"></i>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40 font-heading leading-none">Phase 4</h3>
                        <DisclaimerTooltip className="block mt-1">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading block">Break-Even Projection</span>
                        </DisclaimerTooltip>
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#322D29] border border-[#F26522]/30 text-white rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/90 whitespace-pre-wrap font-dm">{mathData.breakEvenAnalysis}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-white/10 rounded-xl bg-[#3a3430]/40">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-heading">Initialize deep-dive financial modeling to reveal launch math.</p>
                </div>
              )}
            </div>
          )
        )}

          {activeParentTab === 'launchpad' && activeChildTab === 'sites' && (
            !isPremium ? (
              <LockedHubView 
                title="Site Recon" 
                description="Unlock tactical site intelligence, micro-location fit, and demographic alignment reports."
                requiredTier="Premium"
                onUpgrade={onUpgradePrompt}
              />
            ) : (
            <div className="space-y-12 animate-fade-in">
              <div className="flex flex-col items-center space-y-3 py-4">
                <div className="w-12 h-1 bg-[#F26522] rounded-full"></div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-white font-heading">Boots on the Ground Report</h3>
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest font-heading">Tactical Site Intelligence for {idea.locationName || 'Target District'}</p>
              </div>

              {loadingRecon ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Conducting Tactical Site Reconnaissance...</p>
                </div>
              ) : reconData ? (
                <div className="space-y-8">
                  {[
                    { title: 'Micro-Location Fit', content: reconData.microLocationFit, icon: 'fa-shoe-prints' },
                    { title: 'Accessibility & Logistics', content: reconData.accessibilityLogistics, icon: 'fa-truck-fast' },
                    { title: 'Demographic Alignment', content: reconData.demographicAlignment, icon: 'fa-users-viewfinder' },
                    { title: 'Visual Landscape', content: reconData.visualLandscape, icon: 'fa-eye' }
                  ].map((section, idx) => (
                    <div key={`site-recon-${section.title}-${idx}`} className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#322D29] border border-white/10 text-[#F26522] flex items-center justify-center text-xs rounded-lg">
                          <i className={`fas ${section.icon}`}></i>
                        </div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">{section.title}</h4>
                      </div>
                      <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-1 h-full bg-[#F26522]/30 group-hover:bg-[#F26522] transition-colors"></div>
                        <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{section.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-white/10 rounded-xl bg-[#3a3430]/40">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-heading">Initialize tactical recon to reveal site intelligence.</p>
                </div>
              )}
            </div>
          )
        )}

          {activeParentTab === 'launchpad' && activeChildTab === 'risk' && (
            !isPremium ? (
              <LockedHubView 
                title="Risk Guard" 
                description="Unlock operational vulnerability assessments, regulatory analysis, and financial stress tests."
                requiredTier="Premium"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-12 animate-fade-in">
              <div className="flex flex-col items-center py-8">
                <DisclaimerTooltip>
                  <span className={`text-8xl font-black font-heading ${getRiskColor(idea.riskGuard?.score || 5)}`}>{idea.riskGuard?.score || 5}</span>
                </DisclaimerTooltip>
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40 mt-2 font-heading">Risk Resilience</span>
              </div>

              {loadingRisk ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Running Deep-Dive Risk Analysis...</p>
                </div>
              ) : riskData ? (
                <div className="space-y-8">
                  {/* Regulatory & Zoning Analysis */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-4 bg-[#F26522] rounded-full"></div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Regulatory & Zoning Analysis</h3>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{riskData.regulatoryZoning}</p>
                    </div>
                  </div>

                  {/* Market Saturation & Competitive Counter-Moves */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-4 bg-[#F26522] rounded-full"></div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Market Saturation & Competitive Counter-Moves</h3>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{riskData.marketSaturation}</p>
                    </div>
                  </div>

                  {/* Operational & Supply Chain Vulnerabilities */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-4 bg-[#F26522] rounded-full"></div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Operational & Supply Chain Vulnerabilities</h3>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{riskData.operationalSupplyChain}</p>
                    </div>
                  </div>

                  {/* Financial Stress Test */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-4 bg-[#F26522] rounded-full"></div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Financial Stress Test</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        { title: 'Best Case Scenario', content: riskData.financialStressTest.bestCase, color: 'border-l-emerald-500' },
                        { title: 'Expected Case Scenario', content: riskData.financialStressTest.expectedCase, color: 'border-l-amber-500' },
                        { title: 'Worst Case Scenario', content: riskData.financialStressTest.worstCase, color: 'border-l-rose-500' }
                      ].map((scenario, idx) => (
                        <div key={`scenario-${scenario.title}-${idx}`} className={`p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] border-l-4 ${scenario.color} rounded-xl shadow-lg space-y-2`}>
                          <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">{scenario.title}</span>
                          <p className="text-sm font-medium leading-relaxed text-white/80 whitespace-pre-wrap font-dm">{scenario.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mitigation Roadmap */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-4 bg-[#F26522] rounded-full"></div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Mitigation Roadmap</h3>
                    </div>
                    <div className="p-6 sm:p-8 bg-[#322D29] border border-[#F26522]/30 text-white rounded-xl shadow-xl">
                      <p className="text-sm font-medium leading-relaxed text-white/90 whitespace-pre-wrap font-dm">{riskData.mitigationRoadmap}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-white/10 rounded-xl bg-[#3a3430]/40">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-heading">Initialize deep-dive analysis to reveal risk intelligence.</p>
                </div>
              )}
            </div>
          )
        )}

          {activeParentTab === 'launchpad' && activeChildTab === 'strategy' && (
            !isPremium ? (
              <LockedHubView 
                title="18-Month Strategy" 
                description="Unlock full-scale venture evolution roadmaps, pivot points, and trend analysis."
                requiredTier="Premium"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-16 animate-fade-in">
              <div className="flex flex-col items-center space-y-3 py-4">
                <div className="w-12 h-1 bg-[#F26522] rounded-full"></div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-white font-heading">18-Month Scaling Strategy</h3>
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest font-heading">Venture Evolution & Market Dominance Roadmap</p>
              </div>

              {loadingScaling ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Architecting Scaling Strategy...</p>
                </div>
              ) : scalingData ? (
                <div className="space-y-16">
                  {/* The First 90 Days */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] flex items-center justify-center text-base rounded-xl">
                        <i className="fas fa-rocket"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">The First 90 Days</h4>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading mt-0.5 block">From Incorporation to Soft Launch</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      {scalingData.first90Days.map((milestone, idx) => (
                        <div key={`milestone-${milestone.week}-${idx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl relative overflow-hidden group">
                          <div className="absolute top-0 left-0 w-1 h-full bg-[#F26522]/30 group-hover:bg-[#F26522] transition-colors"></div>
                          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                            <span className="text-[10px] font-bold text-[#F26522] uppercase tracking-widest pt-1 font-heading">{milestone.week}</span>
                            <div className="space-y-2">
                              <h5 className="text-sm font-bold uppercase tracking-tight text-white font-heading">{milestone.milestone}</h5>
                              <p className="text-sm font-medium leading-relaxed text-white/80 font-dm">{milestone.howTo}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quarterly Scaling */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] flex items-center justify-center text-base rounded-xl">
                        <i className="fas fa-chart-line"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Quarterly Scaling</h4>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading mt-0.5 block">Growth Velocity Milestones</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      {scalingData.quarterlyScaling.map((goal, idx) => (
                        <div key={`goal-${goal.period}-${idx}`} className="p-6 sm:p-8 bg-[#322D29] border border-white/[0.06] text-white rounded-xl shadow-xl relative overflow-hidden group">
                          <div className="absolute top-0 left-0 w-1 h-full bg-[#F26522]/30 group-hover:bg-[#F26522] transition-colors"></div>
                          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                            <span className="text-[10px] font-bold text-[#F26522] uppercase tracking-widest pt-1 font-heading">{goal.period}</span>
                            <div className="space-y-2">
                              <h5 className="text-sm font-bold uppercase tracking-tight text-white font-heading">{goal.goal}</h5>
                              <p className="text-sm font-medium leading-relaxed text-white/70 font-dm">{goal.howTo}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trend Analysis */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] flex items-center justify-center text-base rounded-xl">
                        <i className="fas fa-bolt"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Trend Analysis (2026-2027)</h4>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading mt-0.5 block">Disruptive Technologies & Consumer Shifts</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      {scalingData.trendAnalysis.map((trend, idx) => (
                        <div key={`trend-${trend.trend}-${idx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl space-y-4">
                          <h5 className="text-sm font-bold uppercase tracking-tight text-white border-b border-white/[0.06] pb-2 font-heading">{trend.trend}</h5>
                          <p className="text-sm font-medium leading-relaxed text-white/80 font-dm">{trend.description}</p>
                          <div className="p-4 bg-[#322D29] border-l-2 border-[#F26522] rounded-r-lg">
                             <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 block mb-1 font-heading">Strategic Impact</span>
                             <p className="text-xs font-medium text-white/90 font-dm">{trend.impact}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pivot Points */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#3a3430] border border-white/10 text-[#F26522] flex items-center justify-center text-base rounded-xl">
                        <i className="fas fa-arrows-split-up"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">Pivot Points</h4>
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading mt-0.5 block">Contingency Strategic Shifts</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {scalingData.pivotPoints.map((pivot, idx) => (
                        <div key={`pivot-${pivot.scenario}-${idx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl space-y-3">
                          <div className="space-y-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Scenario</span>
                            <p className="text-sm font-bold text-white font-heading">{pivot.scenario}</p>
                          </div>
                          <div className="space-y-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">Pivot Strategy</span>
                            <p className="text-sm font-medium leading-relaxed text-white/80 font-dm">{pivot.strategy}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-white/10 rounded-xl bg-[#3a3430]/40">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-heading">Initialize scaling strategy to reveal growth intelligence.</p>
                </div>
              )}
            </div>
          )
        )}
          {activeParentTab === 'intelligence' && (
            !isPremium ? (
              <LockedHubView 
                title="Intelligence Hub" 
                description="Unlock deeper venture intelligence with advanced market analysis and operational planning tools."
                requiredTier="Premium"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-12 animate-fade-in">
              <div className="space-y-2">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Intelligence Hub</h3>
                <p className="text-sm font-medium text-white/60 font-dm">AI-powered competitive intelligence and market positioning analytics.</p>
              </div>

              <LivingSwot 
                idea={idea} 
                hub="discovery" 
                onUpgradePrompt={onUpgradePrompt} 
                intelligenceData={intelligenceData || undefined}
              />
              
              {loadingIntelligence ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Synthesizing Market Intelligence...</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Interactive Intelligence Tools */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Persona Generator */}
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl text-white space-y-6 shadow-xl">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Persona Generator</h4>
                        <button className="text-[9px] font-bold uppercase tracking-widest bg-[#F26522] hover:bg-[#F26522]/90 text-white px-3 py-1.5 rounded-lg transition-all cursor-pointer font-heading">Regenerate</button>
                      </div>
                      <div className="flex gap-4 items-start">
                        <div className="w-14 h-14 bg-[#322D29] border border-white/10 rounded-xl flex items-center justify-center text-xl text-[#F26522]">
                          <i className="fas fa-user-astronaut"></i>
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-sm font-bold uppercase tracking-tight font-heading">The "Urban Nomad" Professional</p>
                          <div className="flex flex-wrap gap-2">
                            <span className="text-[8px] font-bold bg-white/5 border border-white/10 px-2.5 py-1 uppercase tracking-widest text-white/70 rounded">Ages 24-38</span>
                            <span className="text-[8px] font-bold bg-white/5 border border-white/10 px-2.5 py-1 uppercase tracking-widest text-white/70 rounded">High Mobility</span>
                            <span className="text-[8px] font-bold bg-white/5 border border-white/10 px-2.5 py-1 uppercase tracking-widest text-white/70 rounded">Tech-First</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2 pt-4 border-t border-white/[0.06]">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-[#F26522]/80 font-heading">Psychographic Profile</p>
                        <p className="text-xs font-medium text-white/70 leading-relaxed italic font-dm">Values efficiency over ownership. Spends 40% of disposable income on "Experience Services". Highly influenced by review sentiment and localized authenticity.</p>
                      </div>
                    </div>

                    {/* Sentiment Analysis Feed */}
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl space-y-6">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white font-heading">Sentiment Analysis Feed</h4>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                          <span className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest font-heading">Live Feed</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex gap-3 items-start border-b border-white/[0.06] pb-3">
                          <div className="text-emerald-400 text-xs mt-0.5"><i className="fas fa-circle-check"></i></div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-white/90 italic font-dm">"Finally something that understands the local logistics gap in this neighborhood."</p>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Source: Local Community Forum • 2m ago</p>
                          </div>
                        </div>
                        <div className="flex gap-3 items-start border-b border-white/[0.06] pb-3">
                          <div className="text-[#F26522] text-xs mt-0.5"><i className="fas fa-circle-exclamation"></i></div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-white/90 italic font-dm">"Pricing seems high compared to the existing options, but the tech integration is a plus."</p>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Source: Industry Review • 15m ago</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <div className="text-center">
                          <p className="text-xl font-black text-white tracking-tight font-heading">84%</p>
                          <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Positive</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-black text-white tracking-tight font-heading">12%</p>
                          <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Neutral</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-black text-white tracking-tight font-heading">4%</p>
                          <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Negative</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    { [
                        { title: 'Competitor Deep-Dive', content: intelligenceData?.competitorDeepDive, icon: 'fa-users-viewfinder' },
                        { title: 'Differentiation Engine', content: intelligenceData?.differentiationEngine, icon: 'fa-lightbulb' },
                        { title: 'Market Share Estimation', content: intelligenceData?.marketShareEstimation, icon: 'fa-chart-pie' },
                        { title: 'Seasonality Forecasting', content: intelligenceData?.seasonalityForecasting, icon: 'fa-calendar-days' },
                        { title: 'Gentrification Trajectory', content: intelligenceData?.gentrificationTrajectory, icon: 'fa-arrow-trend-up' },
                        { title: 'Economic Cycle Resilience', content: intelligenceData?.economicCycleResilience, icon: 'fa-shield-halved' },
                        { title: 'Persona Generator', content: intelligenceData?.personaGenerator, icon: 'fa-user-gear' },
                        { title: 'Foot Traffic Patterns', content: intelligenceData?.footTrafficPatterns, icon: 'fa-shoe-prints' },
                        { title: 'Sentiment Analysis', content: intelligenceData?.sentimentAnalysis, icon: 'fa-face-smile' }
                      ].map((item, idxx) => {
                        if (!item || !item.title) return null;
                        return (
                        <div key={`intel-hub-${item.title}-${idxx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-lg space-y-3 group hover:border-white/20 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#322D29] border border-white/10 text-[#F26522] flex items-center justify-center text-xs rounded-lg">
                              <i className={`fas ${item.icon}`}></i>
                            </div>
                            <h5 className="text-sm font-bold text-white font-heading uppercase tracking-tight">{item.title}</h5>
                          </div>
                          <p className="text-xs font-medium leading-relaxed text-white/70 italic font-dm">
                            {item.content || 'AI is currently processing deep-dive intelligence for this sector...'}
                          </p>
                        </div>
                      )})}
                  </div>
                </div>
              )}
            </div>
          )
        )}

          {activeParentTab === 'operations' && (
            !isOptimize ? (
              <LockedHubView 
                title="Operations Hub" 
                description="Unlock interactive performance tracking, scenario modeling, and expansion scouting."
                requiredTier="Optimize"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-12 animate-fade-in">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Operations Hub</h3>
                  <p className="text-sm font-medium text-white/60 font-dm">Full-stack operational modeling with live performance tracking and scenario simulation.</p>
                </div>
                <button 
                  onClick={regenerateOperations}
                  disabled={regeneratingOps || loadingOperations}
                  className="px-4 py-2 bg-[#F26522] text-white text-[9px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 cursor-pointer font-heading"
                >
                  {regeneratingOps || loadingOperations ? (
                    <><i className="fas fa-circle-notch animate-spin"></i> Loading...</>
                  ) : (
                    <><i className="fas fa-rotate"></i> Regenerate Analysis</>
                  )}
                </button>
              </div>

              <LivingSwot 
                idea={idea} 
                hub="launchpad" 
                onUpgradePrompt={onUpgradePrompt} 
                operationsData={operationsData || undefined}
              />
              
              {loadingOperations ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Configuring Operational Protocols...</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Interactive Operations Tools */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Performance Benchmarking */}
                    <div className="lg:col-span-2 p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white font-heading">Performance Benchmarking</h4>
                          <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">AI Projections vs. Venture Actuals</p>
                        </div>
                        <div className="flex gap-1.5 p-1 bg-[#322D29] rounded-lg border border-white/[0.06]">
                          <button 
                            onClick={() => setBenchmarkMetric('revenue')}
                            className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest rounded transition-all cursor-pointer font-heading ${benchmarkMetric === 'revenue' ? 'bg-[#F26522] text-white' : 'text-white/40 hover:text-white'}`}
                          >Revenue</button>
                          <button 
                            onClick={() => setBenchmarkMetric('customers')}
                            className={`px-3 py-1 text-[8px] font-bold uppercase tracking-widest rounded transition-all cursor-pointer font-heading ${benchmarkMetric === 'customers' ? 'bg-[#F26522] text-white' : 'text-white/40 hover:text-white'}`}
                          >Customers</button>
                        </div>
                      </div>
                      
                      <div className="h-[240px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={actualsData.map(d => ({
                          month: d.month,
                          ai: benchmarkMetric === 'revenue' ? d.ai : Math.round(d.ai / 30),
                          actual: benchmarkMetric === 'revenue' ? d.actual : d.customers
                        }))}>
                            <defs>
                              <linearGradient id="colorAi" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#F26522" stopOpacity={0.25}/>
                                <stop offset="95%" stopColor="#F26522" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#FFFFFF" stopOpacity={0.2}/>
                                <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: 'rgba(255,255,255,0.4)' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: 'rgba(255,255,255,0.4)' }} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#322D29', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                              itemStyle={{ fontSize: '10px', fontWeight: 'bold' }}
                            />
                            <Legend iconType="square" wrapperStyle={{ fontSize: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em', paddingTop: '20px' }} />
                            <Area type="monotone" dataKey="ai" name="AI Projection" stroke="#F26522" strokeWidth={2.5} fillOpacity={1} fill="url(#colorAi)" />
                            <Area type="monotone" dataKey="actual" name="Venture Actuals" stroke="#FFFFFF" strokeWidth={2.5} fillOpacity={1} fill="url(#colorActual)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                          <span className="text-[9px] font-bold uppercase tracking-widest text-white font-heading">Performance: +12.7% Above AI Baseline</span>
                        </div>
                        <button className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] hover:underline cursor-pointer font-heading">Input Latest Actuals</button>
                      </div>
                    </div>

                    {/* Scenario Modeling */}
                    <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-6 shadow-xl">
                      <div className="space-y-1">
                        <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white font-heading">Scenario Modeling</h4>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Run What-If Analyses</p>
                      </div>
                      
                      <div className="space-y-5">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <label className="text-[9px] font-bold uppercase tracking-widest text-white/50 font-heading">Funding Level</label>
                            <span className="text-[9px] font-bold text-[#F26522] font-heading">${scenarioFunding}k</span>
                          </div>
                          <input 
                            type="range" 
                            min="50" 
                            max="1000" 
                            step="25"
                            value={scenarioFunding}
                            onChange={(e) => setScenarioFunding(Number(e.target.value))}
                            className="w-full accent-[#F26522] h-1.5 bg-[#322D29] rounded-lg appearance-none cursor-pointer" 
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <label className="text-[9px] font-bold uppercase tracking-widest text-white/50 font-heading">Market Shift (Bear/Bull)</label>
                            <span className="text-[9px] font-bold text-white font-heading">+{scenarioMarketShift}%</span>
                          </div>
                          <input 
                            type="range" 
                            min="-50" 
                            max="50" 
                            step="5"
                            value={scenarioMarketShift}
                            onChange={(e) => setScenarioMarketShift(Number(e.target.value))}
                            className="w-full accent-[#F26522] h-1.5 bg-[#322D29] rounded-lg appearance-none cursor-pointer" 
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <label className="text-[9px] font-bold uppercase tracking-widest text-white/50 font-heading">Regulatory Friction</label>
                            <span className="text-[9px] font-bold text-white font-heading">{scenarioRegulatory === 1 ? 'Low' : scenarioRegulatory === 2 ? 'Medium' : 'High'}</span>
                          </div>
                          <input 
                            type="range" 
                            min="1" 
                            max="3" 
                            step="1"
                            value={scenarioRegulatory}
                            onChange={(e) => setScenarioRegulatory(Number(e.target.value))}
                            className="w-full accent-[#F26522] h-1.5 bg-[#322D29] rounded-lg appearance-none cursor-pointer" 
                          />
                        </div>
                      </div>

                      <div className="p-4 bg-[#322D29] border border-white/[0.06] rounded-xl space-y-2">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">AI Impact Forecast</p>
                        {generatingScenario ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 border-2 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-xs font-medium text-white/60 font-dm">Synthesizing scenario forecast...</p>
                          </div>
                        ) : scenarioReport ? (
                          <p className="text-xs font-medium text-white/80 leading-relaxed font-dm">{scenarioReport}</p>
                        ) : (
                          <p className="text-xs font-medium text-white/60 leading-relaxed font-dm">Adjust parameters above and click Generate to see your AI Impact Forecast.</p>
                        )}
                      </div>

                      <button 
                        onClick={async () => {
                          setGeneratingScenario(true);
                          try {
                            const { callGemini } = await import('@/src/services/apiServices');
                            const prompt = `You are eNvesta Operations Hub Scenario Modeler. For venture "${idea.title}" in ${idea.locationName || 'this location'}, with funding=$${scenarioFunding}k, market shift=${scenarioMarketShift > 0 ? '+' : ''}${scenarioMarketShift}%, regulatory friction=${scenarioRegulatory === 1 ? 'Low' : scenarioRegulatory === 2 ? 'Medium' : 'High'}, write a concise 2-paragraph forecast about break-even timeline impact, risk guard score change, and strategic recommendation. Be specific and quantitative.`;
                            const res = await callGemini({
                              model: 'gemini-2.5-flash',
                              contents: [{ role: 'user', parts: [{ text: prompt }] }],
                              config: { maxOutputTokens: 1024 }
                            });
                            setScenarioReport(res.text);
                          } catch (e) {
                            console.error('Scenario generation failed:', e);
                            setScenarioReport('Unable to generate forecast. Please try again.');
                          } finally {
                            setGeneratingScenario(false);
                          }
                        }}
                        disabled={generatingScenario}
                        className="w-full py-3 bg-[#F26522] text-white text-[9px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 rounded-lg transition-colors disabled:opacity-50 cursor-pointer font-heading"
                      >{generatingScenario ? 'Generating...' : 'Generate Detailed Report'}</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    { [
                        { title: 'Scenario Modeling', content: operationsData?.scenarioModeling, icon: 'fa-diagram-project' },
                        { title: 'Investor Matchmaking', content: operationsData?.investorMatchmaking, icon: 'fa-handshake' },
                        { title: 'Grant & Incentive Scanner', content: operationsData?.grantScanner, icon: 'fa-magnifying-glass-dollar' },
                        { title: 'Labor Market Analytics', content: operationsData?.laborMarketAnalytics, icon: 'fa-people-group' },
                        { title: 'Supply Chain Mapping', content: operationsData?.supplyChainMapping, icon: 'fa-truck-fast' },
                        { title: 'Regulatory Navigator', content: operationsData?.regulatoryNavigator, icon: 'fa-gavel' },
                        { title: 'Performance Benchmarking', content: operationsData?.performanceBenchmarking, icon: 'fa-gauge-high' },
                        { title: 'Expansion Scouting', content: operationsData?.expansionScouting, icon: 'fa-map-location-dot' },
                        { title: 'Exit Strategy Modeling', content: operationsData?.exitStrategyModeling, icon: 'fa-door-open' }
                      ].map((item, idxx) => {
                        if (!item || !item.title) return null;
                        return (
                        <div key={`ops-hub-${item.title}-${idxx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-lg space-y-3 group hover:border-white/20 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#322D29] border border-white/10 text-[#F26522] flex items-center justify-center text-xs rounded-lg">
                              <i className={`fas ${item.icon}`}></i>
                            </div>
                            <h5 className="text-sm font-bold text-white font-heading uppercase tracking-tight">{item.title}</h5>
                          </div>
                          <p className="text-xs font-medium leading-relaxed text-white/70 italic font-dm">
                            {item.content || 'Initialize Operations Analysis to populate this module.'}
                          </p>
                        </div>
                      )})}
                  </div>
                </div>
              )}
            </div>
          )
        )}

          {activeParentTab === 'collaboration' && (
            !isTeam ? (
              <LockedHubView 
                title="Collaboration Hub" 
                description="Enable multi-user venture architecture with secure stakeholder sharing and version control."
                requiredTier="Team"
                onUpgrade={onUpgradePrompt}
              />
            ) : showLegalUnacknowledgedState ? (
              <LockedLegalView />
            ) : (
            <div className="space-y-12 animate-fade-in">
              <div className="space-y-2">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Collaboration Hub</h3>
                <p className="text-sm font-medium text-white/60 font-dm">Stakeholder engagement, ethical safeguards, and enterprise integrations.</p>
              </div>
              
              {loadingCollaboration ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-[#3a3430] border border-white/[0.06] rounded-xl">
                  <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 animate-pulse font-heading">Establishing Collaboration Safeguards...</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Interactive Enterprise Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Privacy & Sharing */}
                    <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4 shadow-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Privacy Protocol</span>
                        <i className="fas fa-shield-halved text-[#F26522] text-xs"></i>
                      </div>
                      <select className="w-full bg-[#322D29] border border-white/10 rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white outline-none focus:border-[#F26522] transition-colors">
                        <option>Private (Owner Only)</option>
                        <option>Shared (Stakeholders)</option>
                        <option>Public (Discovery Hub)</option>
                      </select>
                      <button className="w-full py-2.5 bg-[#F26522] text-white text-[9px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 rounded-lg transition-colors cursor-pointer font-heading">Generate Secure Link</button>
                    </div>

                    {/* Version Control */}
                    <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4 shadow-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Version Control</span>
                        <i className="fas fa-code-branch text-[#F26522] text-xs"></i>
                      </div>
                      <div className="space-y-2 max-h-[60px] overflow-y-auto custom-scrollbar pr-2">
                        <div className="flex items-center justify-between text-[9px] font-medium text-white/80 border-b border-white/[0.06] pb-1">
                          <span>v1.2 - Market Pivot</span>
                          <span className="text-white/40">2h ago</span>
                        </div>
                        <div className="flex items-center justify-between text-[9px] font-medium text-white/50">
                          <span>v1.1 - Initial Analysis</span>
                          <span>5h ago</span>
                        </div>
                      </div>
                      <button className="w-full py-2.5 border border-white/10 text-white text-[9px] font-bold uppercase tracking-widest hover:bg-white/5 rounded-lg transition-colors cursor-pointer font-heading">Create Snapshot</button>
                    </div>

                    {/* CRM Sync */}
                    <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4 shadow-xl">
                      <div className="flex items-center justify-between col-span-1 border-b border-white/[0.06] pb-2">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Enterprise CRM Sync</span>
                        <i className="fas fa-plug text-[#F26522] text-xs"></i>
                      </div>

                      <div className="flex gap-2 p-1 bg-[#322D29] rounded-lg border border-white/[0.06]">
                        <button 
                          onClick={() => { setSelectedCrm('hubspot'); setCrmSyncResult(null); }}
                          className={`flex-1 py-1.5 text-[8.5px] font-bold uppercase tracking-wider rounded transition-all flex items-center justify-center gap-1.5 cursor-pointer font-heading ${selectedCrm === 'hubspot' ? 'bg-[#F26522] text-white' : 'text-white/40 hover:text-white'}`}
                        >
                          <i className="fab fa-hubspot"></i> HubSpot
                        </button>
                        <button 
                          onClick={() => { setSelectedCrm('salesforce'); setCrmSyncResult(null); }}
                          className={`flex-1 py-1.5 text-[8.5px] font-bold uppercase tracking-wider rounded transition-all flex items-center justify-center gap-1.5 cursor-pointer font-heading ${selectedCrm === 'salesforce' ? 'bg-[#00A1E0] text-white' : 'text-white/40 hover:text-white'}`}
                        >
                          <i className="fab fa-salesforce"></i> Salesforce
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-[8px] font-bold uppercase text-white/50 tracking-wider font-heading">
                          <span>Sandbox Simulation Mode</span>
                          <input 
                            type="checkbox" 
                            checked={crmSandbox}
                            onChange={(e) => setCrmSandbox(e.target.checked)}
                            className="w-3.5 h-3.5 accent-[#F26522] cursor-pointer"
                          />
                        </div>

                        {!crmSandbox && (
                          <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                            {selectedCrm === 'salesforce' && (
                              <div className="space-y-1">
                                <label className="text-[8px] font-bold text-white/40 uppercase tracking-widest font-heading">Instance Base URL</label>
                                <input 
                                  type="text" 
                                  value={crmInstanceUrl}
                                  onChange={(e) => setCrmInstanceUrl(e.target.value)}
                                  placeholder="e.g. https://mycorp.my.salesforce.com" 
                                  className="w-full text-[9px] font-mono p-2 border border-white/10 rounded-lg bg-[#322D29] text-white focus:border-[#F26522] outline-none" 
                                />
                              </div>
                            )}
                            <div className="space-y-1">
                              <label className="text-[8px] font-bold text-white/40 uppercase tracking-widest font-heading">Bearer API Token / Secret</label>
                              <input 
                                type="password" 
                                value={crmApiKey}
                                onChange={(e) => setCrmApiKey(e.target.value)}
                                placeholder="Paste integration key..." 
                                className="w-full text-[9px] font-mono p-2 border border-white/10 rounded-lg bg-[#322D29] text-white focus:border-[#F26522] outline-none" 
                              />
                            </div>
                          </div>
                        )}

                        <button 
                          onClick={executeCrmSync}
                          disabled={crmSyncing}
                          className="w-full py-2.5 bg-[#F26522] text-white hover:bg-[#F26522]/90 text-[8.5px] font-bold uppercase tracking-[0.15em] rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 font-heading"
                        >
                          {crmSyncing ? (
                            <>
                              <i className="fas fa-spinner animate-spin"></i> Syncing Node...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-arrows-rotate"></i> Sync Dossier to CRM
                            </>
                          )}
                        </button>
                      </div>

                      {/* Sync logs and visual telemetry output */}
                      {crmSyncResult && (
                        <div className="p-3 bg-[#322D29] border border-emerald-500/20 rounded-lg space-y-2 animate-in fade-in duration-300">
                          <div className="flex items-center gap-1.5 text-emerald-400 text-[8px] font-bold uppercase tracking-widest font-heading">
                            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                            <span>CONNECTED • MAPPED 201 CREATED</span>
                          </div>
                          <p className="text-[9px] font-medium text-white/80 leading-tight uppercase font-dm">{crmSyncResult.message}</p>
                          <div className="space-y-1 border-t border-white/[0.06] pt-1.5">
                            <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest block font-heading">PIPELINE PAYLOAD MODEL</span>
                            <pre className="text-[8px] font-mono p-1.5 bg-[#3a3430] text-white/70 overflow-x-auto select-all max-h-[80px] custom-scrollbar border border-white/[0.06] rounded block">
                              {JSON.stringify(crmSyncResult.payload, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}

                      {crmError && (
                        <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg space-y-1 text-rose-400 animate-in fade-in duration-200">
                          <p className="text-[8px] font-bold uppercase tracking-widest font-heading">SYNC ERROR</p>
                          <p className="text-[9px] font-medium leading-tight font-dm">{crmError}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Commentary Section */}
                  <div className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-xl space-y-6">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-4">
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.25em] text-white font-heading">Stakeholder Commentary</h4>
                      <span className="text-[8px] font-bold text-emerald-400 uppercase tracking-widest font-heading">3 Active Annotations</span>
                    </div>
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <div className="w-8 h-8 bg-[#322D29] border border-white/10 rounded-lg flex items-center justify-center text-[10px] font-bold text-white font-heading">JS</div>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-white font-heading">John Stakeholder</span>
                            <span className="text-[8px] font-medium text-white/40">10m ago</span>
                          </div>
                          <p className="text-xs font-medium text-white/80 leading-relaxed font-dm">The differentiation strategy looks solid, but we need more data on the seasonality forecasting for Q4.</p>
                        </div>
                      </div>
                      <div className="flex gap-4 pl-8 sm:pl-12">
                        <div className="w-8 h-8 bg-[#F26522] rounded-lg flex items-center justify-center text-[10px] font-bold text-white font-heading">AI</div>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] font-heading">eNvesta AI</span>
                            <span className="text-[8px] font-medium text-white/40">Just now</span>
                          </div>
                          <p className="text-xs font-medium text-white/70 leading-relaxed italic font-dm">I have updated the Seasonality Forecasting module with historical mobility data for the target district. Check the Intelligence Hub for the latest shifts.</p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 flex gap-3">
                      <input 
                        type="text" 
                        placeholder="ADD ANNOTATION OR TAG STAKEHOLDER..." 
                        className="flex-1 bg-[#322D29] border border-white/10 rounded-lg px-4 py-2.5 text-[9px] font-medium uppercase tracking-wider text-white outline-none focus:border-[#F26522] transition-colors" 
                      />
                      <button className="px-6 py-2.5 bg-[#F26522] text-white text-[9px] font-bold uppercase tracking-widest hover:bg-[#F26522]/90 rounded-lg transition-colors cursor-pointer font-heading">Post</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    { [
                        { title: 'Stakeholder Sharing', content: collaborationData?.stakeholderSharing, icon: 'fa-share-nodes' },
                        { title: 'Version Control', content: collaborationData?.versionControl, icon: 'fa-code-branch' },
                        { title: 'Commentary & Annotations', content: collaborationData?.commentaryAnnotations, icon: 'fa-comments' },
                        { title: 'Algorithmic Transparency', content: collaborationData?.algorithmicTransparency, icon: 'fa-eye' },
                        { title: 'Bias Detection', content: collaborationData?.biasDetection, icon: 'fa-scale-balanced' },
                        { title: 'Data Privacy Controls', content: collaborationData?.dataPrivacyControls, icon: 'fa-lock' },
                        { title: 'CRM & ERP Connectors', content: collaborationData?.crmConnectors, icon: 'fa-plug' },
                        { title: 'ERP Integrations', content: collaborationData?.erpIntegrations, icon: 'fa-network-wired' },
                        { title: 'API Access', content: collaborationData?.apiAccess, icon: 'fa-terminal' },
                        { title: 'Rural Market Optimization', content: collaborationData?.ruralMarketOptimization, icon: 'fa-wheat-awn' },
                        { title: 'Full Localization', content: collaborationData?.fullLocalization, icon: 'fa-language' },
                        { title: 'Advanced Offline Mode', content: collaborationData?.advancedOfflineMode, icon: 'fa-wifi-slash' }
                      ].map((item, idxx) => {
                        if (!item || !item.title) return null;
                        return (
                        <div key={`collab-hub-${item.title}-${idxx}`} className="p-6 sm:p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl shadow-lg space-y-3 group hover:border-white/20 transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#322D29] border border-white/10 text-[#F26522] flex items-center justify-center text-xs rounded-lg">
                              <i className={`fas ${item.icon}`}></i>
                            </div>
                            <h5 className="text-sm font-bold text-white font-heading uppercase tracking-tight">{item.title}</h5>
                          </div>
                          <p className="text-xs font-medium leading-relaxed text-white/70 italic font-dm">
                            {item.content || 'AI is currently configuring collaboration protocols for this project...'}
                          </p>
                        </div>
                      )})}
                  </div>
                </div>
              )}
            </div>
          )
        )}
        </main>

        {shouldShowOptimizeNudge && !optimizeActive && (
          <section className="max-w-3xl mx-auto px-8 pb-20">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 sm:p-10 bg-[#3a3430] border border-[#F26522]/30 rounded-xl shadow-2xl space-y-6 relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-[#F26522]"></div>
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#F26522] text-white flex items-center justify-center text-xs rounded-lg">
                      <i className="fas fa-gauge-high"></i>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Optimize Protocol Available</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white font-heading leading-tight uppercase">
                    This venture is ready for the next phase.
                  </h3>
                  <p className="text-sm font-medium text-white/70 leading-relaxed font-dm">
                    Add eNvesta AI Optimize to track actual performance, model expansion scenarios, and get live market updates as you build.
                  </p>
                </div>
                
                <div className="flex flex-col gap-3 min-w-[200px]">
                  <button 
                    onClick={() => setIsOptimizeUpgradeOpen(true)}
                    className="w-full py-3.5 bg-[#F26522] text-white font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 transition-all font-heading cursor-pointer shadow-lg"
                  >
                    Add Optimize — $49/mo
                  </button>
                  <button 
                    onClick={() => window.open('https://envesta.ai/optimize', '_blank')}
                    className="w-full py-3.5 border border-white/10 text-white font-bold uppercase text-[10px] tracking-[0.2em] rounded-lg hover:bg-white/5 transition-all font-heading cursor-pointer"
                  >
                    Learn More
                  </button>
                  <button 
                    onClick={snoozeOptimizeNudge}
                    className="text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors text-center mt-1 cursor-pointer font-heading"
                  >
                    Not building this yet
                  </button>
                </div>
              </div>
            </motion.div>
          </section>
        )}

        <OptimizeUpgradeFlow 
          isOpen={isOptimizeUpgradeOpen} 
          onClose={() => setIsOptimizeUpgradeOpen(false)} 
          initialVentureId={idea.id}
        />
      </div>

      <footer className="py-3 px-6 border-t border-white/[0.06] bg-[#322D29] flex flex-col sm:flex-row justify-start gap-3 shrink-0">
        <button 
          onClick={onToggleSave}
          className={`py-3 px-8 w-full sm:w-auto rounded-lg font-bold uppercase text-[9px] tracking-[0.2em] font-heading transition-all border cursor-pointer ${
            isSaved ? 'bg-rose-500/10 text-rose-400 border-rose-500/30' : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border-white/[0.06]'
          }`}
        >
          {isSaved ? 'Remove Opportunity' : 'Save Opportunity'}
        </button>
        <button 
          onClick={() => {
            setIsOptimizeUpgradeOpen(true);
          }}
          className="bg-[#F26522] text-white py-3 px-8 w-full sm:w-auto rounded-lg font-bold uppercase text-[9px] tracking-[0.2em] shadow-xl hover:bg-[#F26522]/90 font-heading flex items-center justify-center gap-2 cursor-pointer"
        >
          <i className="fas fa-play text-[7px]"></i>
          Mark as Started
        </button>
        <button onClick={onClose} className="bg-white/5 text-white/70 hover:text-white hover:bg-white/10 py-3 px-8 w-full sm:w-auto rounded-lg font-bold uppercase text-[9px] tracking-[0.2em] font-heading cursor-pointer border border-white/[0.06]">Back To Launchpad</button>
      </footer>
    </div>
  );
};

export default BusinessDetail;
